import React, { useRef, useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { XCircleIcon, XIcon } from '@heroicons/react/20/solid';
import { useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import CreateDepartment from './createdepartment';
import AddTask from './addtask';
import Departments from './departments';
import { get } from 'lodash';

export default function RoleCalibration({ trigger }) {
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [numTasks, setNumTasks] = useState(1);
  const [taskInputs, setTaskInputs] = useState(['']);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pay, setPay] = useState('');
  const [payType, setPayType] = useState('Hourly');
  const [contractType, setContractType] = useState('0 Hours');
  const [contractedDays, setContractedDays] = useState('');
  const [contractedHours, setContractedHours] = useState('');
  const [breakLength, setBreakLength] = useState('');
  const breakLengthManuallySet = useRef(false);
  const [roles, setRoles] = useState([]);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingPay, setEditingPay] = useState('');
  const [staff, setStaff] = useState([]);
  const [openDepartment, setOpenDepartment] = useState(false);
  const [travel, setTravel] = useState('');
  const [tasks, setAddTask] = useState(null);
  const [reload, setReload] = useState(false);
  const [ageGroup, setAgeGroup] = useState('21 & over');
  const [needDBS, setNeedDBS] = useState(false);
  const [roleFormMode, setRoleFormMode] = useState('create');
  const [roleFormEditingId, setRoleFormEditingId] = useState(null);
  const [managementLevel, setManagementLevel] = useState('1');
  const [access, setAccess] = useState('staff');
  const [editingAccess, setEditingAccess] = useState('staff');
  const [editingManagementLevel, setEditingManagementLevel] = useState('1');
  const [shiftPatterns, setShiftPatterns] = useState([]);
  const [newPatternStart, setNewPatternStart] = useState('');
  const [newPatternEnd, setNewPatternEnd] = useState('');
  const [editingPatternIdx, setEditingPatternIdx] = useState(null);
  const [editingPatternStart, setEditingPatternStart] = useState('');
  const [editingPatternEnd, setEditingPatternEnd] = useState('');
  const user = useContext(AuthContext);
  const navigate = useNavigate();

  const inputClassName =
    'block w-full rounded-xl border border-indigo-200/70 bg-white/90 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200';
  const cardClassName =
    'rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 p-4 sm:p-6 shadow-[0_12px_30px_rgba(99,102,241,0.2)] backdrop-blur-xl';
  const tableShellClassName =
    'rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-[0_16px_36px_rgba(15,23,42,0.08)]';

  const normalizeManagementLevel = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 4 ? parsed : 1;
  };

  const resolveRoleAccess = (role) => {
    const normalizedAccess = String(role?.Access || '').trim().toLowerCase();
    if (['admin', 'manager', 'staff'].includes(normalizedAccess)) return normalizedAccess;
    const resolvedManagementLevel = normalizeManagementLevel(role?.ManagementLevel);
    if (resolvedManagementLevel >= 4) return 'admin';
    if (resolvedManagementLevel > 1) return 'manager';
    return 'staff';
  };

  const parseNullableFloat = (value) => {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text) return null;
    const parsed = Number.parseFloat(text.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatGBP = (value) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const parsedPayRate = parseNullableFloat(pay);
  const parsedContractedDays = parseNullableFloat(contractedDays);
  const parsedContractedHours = parseNullableFloat(contractedHours);
  const canShowCompCalculator =
    contractType !== '0 Hours' &&
    parsedPayRate != null &&
    parsedPayRate > 0 &&
    parsedContractedDays != null &&
    parsedContractedDays > 0 &&
    parsedContractedHours != null &&
    parsedContractedHours > 0;
  const estimatedYearlyPay = (parsedPayRate || 0) * (parsedContractedHours || 0) * 52;
  const estimatedMonthlyPay = estimatedYearlyPay / 12;

  useEffect(() => {
    const days = parseFloat(contractedDays);
    const hours = parseFloat(contractedHours);
    if (days > 0 && hours > 0) {
      const avg = hours / days;
      if (avg >= 6 && (!breakLength || breakLength === '0')) {
        setBreakLength('20');
      }
    }
  }, [contractedDays, contractedHours]);

  const handleTaskCountChange = (event) => {
    const count = parseInt(event.target.value, 10);
    setNumTasks(count);
    setTaskInputs(Array(count).fill(''));
  };

  const handleTaskChange = (index, value) => {
    const updatedTasks = [...taskInputs];
    updatedTasks[index] = value;
    setTaskInputs(updatedTasks);
  };

  const handleSave = async () => {
    const defaultTask = 'Training';
    const normalizedTasks = taskInputs
      .map((task) => (task || '').toString().trim())
      .filter(Boolean);
    if (travel && travel !== 'None' && !normalizedTasks.includes(travel)) {
      normalizedTasks.push(travel);
    }
    if (!normalizedTasks.includes(defaultTask)) {
      normalizedTasks.push(defaultTask);
    }
    const resolvedContractedDays = contractType === '0 Hours' ? '0' : contractedDays;
    const resolvedContractedHours = contractType === '0 Hours' ? '0' : contractedHours;
    const normalizedPayRate = parseNullableFloat(pay);
    if (pay !== '' && normalizedPayRate == null) {
      alert('Please enter a valid numeric Pay Rate.');
      return;
    }

    const newRole = {
      CompanyCode: user.companyCode,
      Name: name,
      Description: description,
      Tasks: normalizedTasks.join(','),
      PayRate: normalizedPayRate != null ? Number(normalizedPayRate.toFixed(2)) : null,
      PayType: payType,
      ContractType: contractType,
      ContractedDays: resolvedContractedDays,
      ContractedHours: resolvedContractedHours,
      BreakLength: breakLength,
      AgeGroup: ageGroup,
      NeedDBS: needDBS ? 1 : null,
      Access: access,
      ManagementLevel: normalizeManagementLevel(managementLevel),
      ShiftPatterns: shiftPatterns.filter((p) => p.start && p.end).map((p) => `${p.start}-${p.end}`).join(',') || null,
    };

    try {
      const isEditMode = roleFormMode === 'edit' && roleFormEditingId;
      const response = await fetch(
        isEditMode
          ? `https://api.insightos.co.uk/api/roles/${roleFormEditingId}`
          : 'https://api.insightos.co.uk/api/roles',
        {
          method: isEditMode ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newRole),
        }
      );
      if (!response.ok) throw new Error('Failed to save role');

      setOpenRoleModal(false);
      setName('');
      setDescription('');
      setNumTasks(1);
      setTaskInputs(['']);
      setTravel('');
      setNeedDBS(false);
      setPay('');
      setPayType('Hourly');
      setContractType('0 Hours');
      setContractedDays('');
      setContractedHours('');
      setBreakLength('');
      breakLengthManuallySet.current = false;
      setAgeGroup('21 & over');
      setAccess('staff');
      setManagementLevel('1');
      setRoleFormMode('create');
      setRoleFormEditingId(null);
      setShiftPatterns([]);
      setNewPatternStart('');
      setNewPatternEnd('');
      setEditingPatternIdx(null);
      getRoles();
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  const getRoles = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/allroles', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch roles');
      const data = await response.json();
      setRoles(data.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    const getStaff = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/staff', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch staff');
        const data = await response.json();
        const staffData = data.data;
        const staffWithRole = staffData.map((staff) => {
          const role = roles.find((role) => role.Name === staff.Role);
          return { ...staff, Role: role?.Name || 'No Role' };
        });
        setStaff(staffWithRole);
      } catch (error) {
        console.error('Error fetching staff:', error);
      }
    };
    getStaff();
  }, [roles]);

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/roles/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to delete role');
      const updatedRoles = roles.filter((role) => role.id !== id);
      setRoles(updatedRoles);
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };

  const startEditPay = (role) => {
    setEditingRoleId(role.id);
    setEditingPay(role.PayRate || '');
    setEditingAccess(resolveRoleAccess(role));
    setEditingManagementLevel(String(normalizeManagementLevel(role.ManagementLevel)));
  };

  const cancelEditPay = () => {
    setEditingRoleId(null);
    setEditingPay('');
    setEditingAccess('staff');
    setEditingManagementLevel('1');
  };

  const handleSavePayRate = async (roleId) => {
    try {
      const PayRate = parseNullableFloat(editingPay);
      if (editingPay !== '' && PayRate == null) {
        alert('Please enter a valid numeric Pay Rate.');
        return;
      }
      const Access = editingAccess;
      const ManagementLevel = normalizeManagementLevel(editingManagementLevel);
      const response = await fetch(`https://api.insightos.co.uk/api/payroles/${roleId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PayRate, Access, ManagementLevel }),
      });
      if (!response.ok) throw new Error('Failed to update pay rate');
      const updated = roles.map((role) =>
        role.id === roleId
          ? {
              ...role,
              PayRate: editingPay,
              Access,
              ManagementLevel,
            }
          : role
      );
      setRoles(updated);
      setEditingRoleId(null);
      setEditingPay('');
      setEditingAccess('staff');
      setEditingManagementLevel('1');
    } catch (error) {
      console.error('Error updating pay rate:', error);
      alert('Failed to update pay rate');
    }
  };

  const handleTaskAdded = () => {
    setReload((prev) => !prev);
    setAddTask(null);
  };

  const openEditRoleModal = (role) => {
    const parsedTasks = String(role?.Tasks || '')
      .split(',')
      .map((task) => task.trim())
      .filter(Boolean);
    setRoleFormMode('edit');
    setRoleFormEditingId(role.id);
    setOpenRoleModal(true);
    setName(role?.Name || '');
    setDescription(role?.Description || '');
    setPay(role?.PayRate != null ? String(role.PayRate) : '');
    setPayType(role?.PayType || 'Hourly');
    setContractType(role?.ContractType || '0 Hours');
    setContractedDays(role?.ContractedDays != null ? String(role.ContractedDays) : '');
    setContractedHours(role?.ContractedHours != null ? String(role.ContractedHours) : '');
    setBreakLength(role?.BreakLength != null ? String(role.BreakLength) : '');
    setAgeGroup(role?.AgeGroup || '21 & over');
    setNeedDBS(Number(role?.NeedDBS || 0) === 1);
    setAccess(resolveRoleAccess(role));
    setManagementLevel(String(normalizeManagementLevel(role?.ManagementLevel)));
    setTaskInputs(parsedTasks.length ? parsedTasks : ['']);
    setNumTasks(parsedTasks.length ? parsedTasks.length : 1);
    setTravel('');
    breakLengthManuallySet.current = true;
    const parsedShiftPatterns = String(role?.ShiftPatterns || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((p) => {
        const dashIdx = p.indexOf('-', p.indexOf(':') + 1);
        const start = dashIdx > 0 ? p.slice(0, dashIdx) : '';
        const end = dashIdx > 0 ? p.slice(dashIdx + 1) : '';
        return { start, end };
      });
    setShiftPatterns(parsedShiftPatterns);
    setNewPatternStart('');
    setNewPatternEnd('');
    setEditingPatternIdx(null);
  };

  const handleAIGenerateResponsibilities = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/generate-responsibilities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: name, description: description }),
      });
      if (!response.ok) throw new Error('Failed to generate responsibilities');
      const data = await response.json();
      const responsibilitiesCount = data.responsibilities.split(',').length;
      setNumTasks(responsibilitiesCount);
      setTaskInputs(data.responsibilities.split(','));
    } catch (error) {
      console.error('Error generating responsibilities:', error);
    }
  };

  const handleCancel = () => {
    setOpenRoleModal(false);
    setAgeGroup('21 & over');
    setRoleFormMode('create');
    setRoleFormEditingId(null);
    setShiftPatterns([]);
    setNewPatternStart('');
    setNewPatternEnd('');
    setEditingPatternIdx(null);
  };

  return (
    <div className="px-3 sm:px-6 lg:px-8 max-w-full mx-auto space-y-6">
      {/* ─── Header ────────────────────────────────────────────────────── */}
      <div className={cardClassName}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-semibold text-indigo-950">Job Roles</h1>
            <p className="mt-2 text-sm text-indigo-900/80">
              Manage the roles and responsibilities of your team members.
            </p>
          </div>
          <div className="flex justify-start sm:justify-end mt-2 sm:mt-0 sm:ml-4">
            <button
              onClick={() => navigate('/recruitment')}
              type="button"
              className="mr-2 w-full sm:w-auto rounded-xl border border-slate-300 bg-white px-4 py-2 text-center text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Create Vacancy
            </button>
            <button
              onClick={() => {
                setRoleFormMode('create');
                setRoleFormEditingId(null);
                setOpenRoleModal(true);
              }}
              type="button"
              className="w-full sm:w-auto rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 px-4 py-2 text-center text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95"
            >
              Add Role
            </button>
          </div>
        </div>
      </div>

      {/* ─── Table ────────────────────────────────────────────────────── */}
      <div className={tableShellClassName}>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100/90">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 sm:pl-4">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Description
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Responsibilities
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Contract Type
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Contracted Days
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Contracted Hours
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Break Length
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Age Group
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Access
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Management Level
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Add Task
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Pay Rate
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Compensation
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Staff Count
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {roles.map((role) => (
                  <tr key={role.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-slate-900 sm:pl-4">
                      {role.Name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-600">
                      {role.Description}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-indigo-600">
                      <ul className="list-disc pl-4">
                        {role.Tasks &&
                          role.Tasks.split(',').map((task, idx) => <li key={idx}>{task.trim()}</li>)}
                      </ul>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-600">
                      {role.ContractType}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {role.ContractedDays}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {role.ContractedHours}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {role.BreakLength} mins
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {role.AgeGroup}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-600">
                      {editingRoleId === role.id ? (
                        <select
                          value={editingAccess}
                          onChange={(e) => setEditingAccess(e.target.value)}
                          className="w-28 rounded-xl border border-indigo-200 px-2 py-1 text-sm"
                        >
                          <option value="staff">Staff</option>
                          <option value="manager">Manager</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold capitalize text-indigo-700">
                          {resolveRoleAccess(role)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {editingRoleId === role.id ? (
                        <select
                          value={editingManagementLevel}
                          onChange={(e) => setEditingManagementLevel(e.target.value)}
                          className="w-40 rounded-xl border border-indigo-200 px-2 py-1 text-sm"
                        >
                          <option value="1">1 - Employee</option>
                          <option value="2">2 - Manager</option>
                          <option value="3">3 - Higher Management</option>
                          <option value="4">4 - Director / C-Suite</option>
                        </select>
                      ) : (
                        normalizeManagementLevel(role.ManagementLevel)
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      {!tasks && (
                        <button
                          onClick={() => setAddTask(role)}
                          className="rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/15 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-900/35 hover:from-emerald-500/95 hover:to-emerald-400/95"
                        >
                          Add New Task
                        </button>
                      )}
                      {tasks && tasks.id === role.id && (
                        <AddTask role={role} setAddTask={setAddTask} onTaskAdded={handleTaskAdded} />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-indigo-700">
                      {editingRoleId === role.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={editingPay}
                            onChange={(e) => setEditingPay(e.target.value)}
                            className="w-28 rounded-xl border border-indigo-200 px-2 py-1 text-sm"
                          />
                          <button
                            onClick={() => handleSavePayRate(role.id)}
                            className="rounded-lg bg-green-600 text-white px-2 py-1 text-xs hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditPay}
                            className="rounded-lg bg-gray-200 text-gray-700 px-2 py-1 text-xs hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">£{role.PayRate} ph</span>
                          {user?.access === 'admin' && (
                            <button
                              onClick={() => startEditPay(role)}
                              className="text-red-600 text-xs font-medium hover:underline"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-violet-500">{role.PayType}</td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {staff.filter((s) => s.Role === role.Name).length}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                      <button
                        onClick={() => openEditRoleModal(role)}
                        type="button"
                        className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        Edit Role
                      </button>
                      <button
                        onClick={() => handleDelete(role.id)}
                        type="button"
                        className="rounded-xl bg-red-600 ml-3 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-500"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ─── INLINE ROLE FORM ──────────────────────────────────────────── */}
      {openRoleModal && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-[0_18px_40px_rgba(30,41,59,0.35)]">
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-slate-900">
                {roleFormMode === 'edit' ? 'Edit Role' : 'Create Role'}
              </h2>
              <button
                type="button"
                onClick={handleCancel}
                className="text-indigo-700/80 hover:text-indigo-950"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-900">
                Name (Unique name for each role)
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Customer Support"
                  className={inputClassName}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <div className="mt-2">
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputClassName}
                />
              </div>
              <button
                type="button"
                onClick={handleAIGenerateResponsibilities}
                className="w-full sm:w-auto mt-2 text-sm px-3 py-2 rounded-xl bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/15 text-white shadow-lg shadow-emerald-900/35 hover:from-emerald-500/95 hover:to-emerald-400/95 transition-colors"
              >
                AI Generate Responsibilities
              </button>
            </div>

            {/* Number of tasks and task inputs */}
            <div>
              <label htmlFor="numberoftasks" className="block text-sm font-medium text-gray-900">
                Responsibilities (You Can Add More Later)
              </label>
              <div className="mt-2 grid grid-cols-1">
                <select
                  id="numberoftasks"
                  name="numberoftasks"
                  value={numTasks}
                  onChange={handleTaskCountChange}
                  className="col-start-1 row-start-1 w-full appearance-none rounded-xl border border-indigo-200/70 bg-white/90 py-2 pl-3 pr-8 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                />
              </div>
            </div>
            <div className="space-y-4 mt-4">
              {taskInputs.map((task, index) => (
                <div key={index}>
                  <input
                    type="text"
                    id={`task-${index}`}
                    name={`task-${index}`}
                    value={task}
                    onChange={(e) => handleTaskChange(index, e.target.value)}
                    placeholder={`Enter task ${index + 1}`}
                    className={inputClassName}
                  />
                </div>
              ))}
            </div>

            {/* Pay Rate */}
            <div>
              <label htmlFor="pay" className="block text-sm font-medium text-gray-900">
                Hourly Rate
              </label>
              <div className="mt-2">
                <input
                  id="pay"
                  name="pay"
                  type="number"
                  step="0.01"
                  value={pay}
                  onChange={(e) => setPay(e.target.value)}
                  placeholder="95.00"
                  className={inputClassName}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Used as hourly rate in calculator: Hourly Rate x Contracted Weekly Hours x 52.
              </p>
              <label htmlFor="payType" className="block text-sm font-medium text-gray-900 mt-3">
                Compensation Type
              </label>
              <div className="mt-2">
                <select
                  id="payType"
                  name="payType"
                  value={payType}
                  onChange={(e) => setPayType(e.target.value)}
                  className={inputClassName}
                >
                  <option value="Hourly">Hourly</option>
                  <option value="Annual">Annual</option>
                </select>
              </div>
            </div>

            {/* Contract details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="contractType" className="block text-sm font-medium text-gray-900 mt-3">
                  Contract Type
                </label>
                <select
                  id="contractType"
                  name="contractType"
                  value={contractType}
                  onChange={(e) => setContractType(e.target.value)}
                  className={inputClassName}
                >
                  <option value="0 Hours">0 Hours</option>
                  <option value="Part Time">Part Time</option>
                  <option value="Contracted Days">Full Time</option>
                </select>
              </div>
              <div>
                {contractType === '0 Hours' && (
                  <p className="text-sm text-gray-500 mt-6">No further details needed for 0 Hour contracts.</p>
                )}
              </div>
              {contractType !== '0 Hours' && (
                <>
                  <div>
                    <label htmlFor="contractedDays" className="block text-sm font-medium text-gray-900 mt-3">
                      Contracted Days
                    </label>
                    <input
                      type="number"
                      id="contractedDays"
                      name="contractedDays"
                      value={contractedDays}
                      onChange={(e) => setContractedDays(e.target.value)}
                      className={inputClassName}
                      min="0"
                    />
                  </div>
                  <div>
                    <label htmlFor="contractedHours" className="block text-sm font-medium text-gray-900 mt-3">
                      Contracted Hours (per week)
                    </label>
                    <input
                      type="number"
                      id="contractedHours"
                      name="contractedHours"
                      value={contractedHours}
                      onChange={(e) => setContractedHours(e.target.value)}
                      className={inputClassName}
                      min="0"
                    />
                  </div>
                </>
              )}
              <div>
                <label htmlFor="breakLength" className="block text-sm font-medium text-gray-900 mt-3">
                  Break Length (minutes)
                </label>
                <input
                  type="number"
                  id="breakLength"
                  name="breakLength"
                  value={breakLength}
                  onChange={(e) => {
                    setBreakLength(e.target.value);
                    breakLengthManuallySet.current = true;
                  }}
                  className={inputClassName}
                  min="0"
                />
                <span className="text-xs text-gray-500">(Auto-set to 20 if contracted hours/day ≥ 6, editable)</span>
              </div>
            </div>

            {/* Compensation calculator */}
            {contractType !== '0 Hours' && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
                <p className="text-sm font-semibold text-emerald-900">Compensation Calculator</p>
                <p className="mt-1 text-xs text-emerald-800/80">
                  Formula: Hourly Rate x Contracted Weekly Hours x 52
                </p>
                {canShowCompCalculator ? (
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <p className="text-sm text-emerald-800">
                      Estimated Monthly Pay: <span className="font-semibold">{formatGBP(estimatedMonthlyPay)}</span>
                    </p>
                    <p className="text-sm text-emerald-800">
                      Estimated Yearly Pay: <span className="font-semibold">{formatGBP(estimatedYearlyPay)}</span>
                    </p>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-emerald-800/80">
                    Enter Hourly Rate and Contracted Weekly Hours to calculate estimated monthly and yearly pay.
                  </p>
                )}
              </div>
            )}

            {/* Travel */}
            <div>
              <label htmlFor="travel" className="block text-sm font-medium text-gray-900">
                Does this role require the staff member to travel outside the primary office?
              </label>
              <div className="mt-2">
                <select
                  id="travel"
                  name="travel"
                  value={travel}
                  onChange={(e) => setTravel(e.target.value)}
                  className={inputClassName}
                >
                  <option value="" disabled>
                    Select travel requirement
                  </option>
                  <option value="Client Visits">Client Visits</option>
                  <option value="Inter Office">Inter-Office Travel</option>
                  <option value="Remote Occasional">Remote Work with Occasional Travel</option>
                  <option value="None">No Travel Required</option>
                </select>
              </div>
            </div>

            {/* Management Level & Access */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label htmlFor="managementLevel" className="block text-sm font-medium text-gray-900 mt-3">
                  Management Level
                </label>
                <select
                  id="managementLevel"
                  name="managementLevel"
                  value={managementLevel}
                  onChange={(e) => setManagementLevel(e.target.value)}
                  className={inputClassName}
                >
                  <option value="1">1 - Employee</option>
                  <option value="2">2 - Manager</option>
                  <option value="3">3 - Higher Management</option>
                  <option value="4">4 - Director / C-Suite</option>
                </select>
              </div>
              <div>
                <label htmlFor="access" className="block text-sm font-medium text-gray-900 mt-3">
                  Access
                </label>
                <select
                  id="access"
                  name="access"
                  value={access}
                  onChange={(e) => setAccess(e.target.value)}
                  className={inputClassName}
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {/* Age Group */}
            <div>
              <label htmlFor="ageGroup" className="block text-sm font-medium text-gray-900">
                Age Group
              </label>
              <div className="mt-2">
                <select
                  id="ageGroup"
                  name="ageGroup"
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  className={inputClassName}
                >
                  <option value="21 & over">21 & over (£12.21)</option>
                  <option value="18–20">18–20 (£10.00)</option>
                  <option value="16–17">16–17 (£7.55)</option>
                  <option value="Apprentice*">Apprentice* (£7.55)</option>
                </select>
              </div>
            </div>

            {ageGroup !== '21 & over' && (
              <div>
                <table className="mt-4 min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Age group</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Minimum hourly rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-3 py-4 text-sm text-gray-500">21 & over</td>
                      <td className="px-3 py-4 text-sm text-gray-500">£12.21</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-4 text-sm text-gray-500">18–20</td>
                      <td className="px-3 py-4 text-sm text-gray-500">£10.00</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-4 text-sm text-gray-500">16–17</td>
                      <td className="px-3 py-4 text-sm text-gray-500">£7.55</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-4 text-sm text-gray-500">Apprentice*</td>
                      <td className="px-3 py-4 text-sm text-gray-500">£7.55</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* DBS Check */}
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={needDBS}
                onClick={() => setNeedDBS((v) => !v)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  needDBS ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    needDBS ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <div>
                <p className="text-sm font-medium text-gray-900">Need DBS Check</p>
                <p className="text-xs text-gray-500">Require a Disclosure and Barring Service check for this role.</p>
              </div>
            </div>

            {/* Shift Patterns */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">Shift Patterns</label>
              <p className="text-xs text-gray-500 mb-3">
                Add recurring shift patterns (e.g. 09:00–17:00). Staff can use these as quick-fill when booking shifts.
              </p>

              {shiftPatterns.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {shiftPatterns.map((pattern, idx) => (
                    <div key={idx} className="inline-flex items-center gap-1">
                      {editingPatternIdx === idx ? (
                        <div className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-1">
                          <input
                            type="time"
                            value={editingPatternStart}
                            onChange={(e) => setEditingPatternStart(e.target.value)}
                            className="border-0 bg-transparent text-xs w-24 focus:outline-none"
                          />
                          <span className="text-indigo-400 text-xs">–</span>
                          <input
                            type="time"
                            value={editingPatternEnd}
                            onChange={(e) => setEditingPatternEnd(e.target.value)}
                            className="border-0 bg-transparent text-xs w-24 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (editingPatternStart && editingPatternEnd) {
                                const updated = [...shiftPatterns];
                                updated[idx] = { start: editingPatternStart, end: editingPatternEnd };
                                setShiftPatterns(updated);
                              }
                              setEditingPatternIdx(null);
                            }}
                            className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold ml-1"
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingPatternIdx(null)}
                            className="text-gray-400 hover:text-gray-600 text-xs ml-0.5"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-1 rounded-full">
                          {pattern.start} – {pattern.end}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingPatternIdx(idx);
                              setEditingPatternStart(pattern.start);
                              setEditingPatternEnd(pattern.end);
                            }}
                            className="ml-1 text-indigo-500 hover:text-indigo-700 text-xs leading-none"
                            title="Edit"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            onClick={() => setShiftPatterns(shiftPatterns.filter((_, i) => i !== idx))}
                            className="ml-0.5 text-indigo-400 hover:text-red-600 text-xs leading-none"
                            title="Delete"
                          >
                            ✕
                          </button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="time"
                  value={newPatternStart}
                  onChange={(e) => setNewPatternStart(e.target.value)}
                  className="border border-indigo-200 rounded-lg px-2 py-1.5 text-sm"
                  placeholder="Start"
                />
                <span className="text-gray-400 text-sm">–</span>
                <input
                  type="time"
                  value={newPatternEnd}
                  onChange={(e) => setNewPatternEnd(e.target.value)}
                  className="border border-indigo-200 rounded-lg px-2 py-1.5 text-sm"
                  placeholder="End"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newPatternStart && newPatternEnd) {
                      setShiftPatterns([...shiftPatterns, { start: newPatternStart, end: newPatternEnd }]);
                      setNewPatternStart('');
                      setNewPatternEnd('');
                    }
                  }}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                >
                  Add Pattern
                </button>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-6 flex flex-col sm:flex-row justify-between gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-xl border border-indigo-200 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-900 shadow-sm hover:bg-white w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/15 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95 w-full sm:w-auto"
              >
                {roleFormMode === 'edit' ? 'Update Role' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}